import { Alerts } from "../../../utils/Alerts.js";
import { RPCDocs } from "../../../services/rpc.js";
import { useCurrentUser } from "../../AuthWrapper2/hooks/useCurrentUser.js";
import { useRPCContext } from "../../../contexts/RPCContext.js";
import { useWatchGeolocation } from "../../../hooks/useGeolocation.js";
import { useScheduleConfig } from "../hooks/useScheduleConfig.js";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Button, ButtonConfirm, ButtonGroup, Icon, Loading } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import { getDevice } from "@therms/web-js";
import { MdGpsOff } from "react-icons/md";
//#region src/modules/Scheduling/shared/ShiftActionsButtonGroup.tsx
var RoleElement = RPCDocs.Auth.GetIdentityInfo.v1.Roles;
function ShiftActionsButtonGroup({ shift }) {
	const geo = useWatchGeolocation();
	const { rpcService } = useRPCContext();
	const currentUser = useCurrentUser();
	const [now, setNow] = useState((/* @__PURE__ */ new Date()).toISOString());
	const [loading, setLoading] = useState(false);
	const { data: scheduleConfig } = useScheduleConfig(void 0, { cacheStrategy: "cache-only" });
	const actions = useMemo(() => {
		if (!shift || !scheduleConfig) return void 0;
		if (shift.status?.timeframe === "current") {
			const lastTimeLogAction = shift.timeLogStatus?.action;
			if (!lastTimeLogAction) return { allowStartShift: true };
			if (lastTimeLogAction === "startshift") return {
				allowEndShift: true,
				allowStartBreak: true
			};
			if (lastTimeLogAction === "startbreak") return {
				allowEndBreak: true,
				allowEndShift: true
			};
			if (lastTimeLogAction === "endbreak") return {
				allowEndShift: true,
				allowStartBreak: true
			};
			if (lastTimeLogAction === "endshift") return { allowStartShift: true };
		} else if (shift.startTime >= now) {
			if (dayjs(shift.startTime).diff(now, "minute") <= (scheduleConfig.settings.allowEarlyShiftStartMin || 0)) return { allowStartShift: true };
		}
	}, [
		geo,
		now,
		scheduleConfig,
		shift
	]);
	const timeLogAction = async (action) => {
		if (!scheduleConfig) {
			Alerts.error("Cannot perform TimeLog action", "Schedule Config is not found/loaded");
			return;
		}
		const device = getDevice();
		if (!currentUser.anp.roles.includes(RoleElement.Scheduling) && scheduleConfig.settings.shiftStartEndGPSRequired) {
			if (!geo.accuracy || geo.timestamp < dayjs().subtract(10, "minute").toISOString()) {
				geo.refresh();
				Alerts.error("GPS Location Required - Please enable GPS or try again in a few minutes");
				return;
			}
		}
		setLoading(true);
		const deviceGeoTag = geo.accuracy ? {
			accuracy: geo.accuracy,
			coords: {
				lat: geo.lat,
				lng: geo.lng
			},
			device,
			timestamp: geo.timestamp
		} : void 0;
		if (action === "startshift") rpcService.Scheduling.StartShift.v1.call({
			deviceGeoTag,
			shiftId: shift.id
		}).catch((err) => {
			Alerts.error("There was a problem Starting Shift", err.message);
		}).finally(() => setLoading(false));
		else if (action === "endshift") rpcService.Scheduling.EndShift.v1.call({
			deviceGeoTag,
			shiftId: shift.id
		}).catch((err) => {
			Alerts.error("There was a problem Ending Shift", err.message);
		}).finally(() => setLoading(false));
		else if (action === "startbreak") rpcService.Scheduling.StartBreak.v1.call({
			deviceGeoTag,
			shiftId: shift.id
		}).catch((err) => {
			Alerts.error("There was a problem Starting Break", err.message);
		}).finally(() => setLoading(false));
		else if (action === "endbreak") rpcService.Scheduling.EndBreak.v1.call({
			deviceGeoTag,
			shiftId: shift.id
		}).catch((err) => {
			Alerts.error("There was a problem Ending Break", err.message);
		}).finally(() => setLoading(false));
		else throw new Error("");
	};
	useEffect(() => {
		const msUntilNextMinute = 6e4 - (/* @__PURE__ */ new Date()).getSeconds() * 1e3;
		const t = setTimeout(() => {
			setNow((/* @__PURE__ */ new Date()).toISOString());
		}, msUntilNextMinute);
		return () => clearTimeout(t);
	}, [now]);
	if (!actions || !shift || !scheduleConfig) return null;
	if (!currentUser.anp.roles.includes(RoleElement.Scheduling)) {
		if (scheduleConfig.settings.shiftStartEndGPSRequired && !(geo.lat && geo.lng) && !geo.loading) return /* @__PURE__ */ jsxs("div", {
			className: "text-color-critical text-center",
			children: [
				/* @__PURE__ */ jsx(Icon, { Icon: MdGpsOff }),
				" GPS Location Required - Please enable GPS or",
				" ",
				/* @__PURE__ */ jsx(Button, {
					onClick: geo.refresh,
					variant: "critical",
					size: "sm",
					children: "Retry GPS"
				})
			]
		});
	}
	return /* @__PURE__ */ jsxs("div", {
		className: "relative",
		children: [!currentUser.anp.roles.includes(RoleElement.Scheduling) && scheduleConfig.settings.shiftStartEndGPSRequired && !(geo.lat && geo.lng) && geo.loading && /* @__PURE__ */ jsx(Loading, {
			overlay: true,
			message: "Loading GPS Coordinates"
		}), /* @__PURE__ */ jsxs(ButtonGroup, {
			fullWidth: true,
			children: [
				actions.allowStartShift && /* @__PURE__ */ jsx(Button, {
					disabled: loading,
					onClick: () => timeLogAction("startshift"),
					variant: "positive",
					children: /* @__PURE__ */ jsx("div", {
						className: "min-w-max",
						children: "Start Shift"
					})
				}),
				actions.allowStartBreak && /* @__PURE__ */ jsx(Button, {
					disabled: loading,
					onClick: () => timeLogAction("startbreak"),
					variant: "main",
					children: /* @__PURE__ */ jsx("div", {
						className: "min-w-max",
						children: "Start Break"
					})
				}),
				actions.allowEndBreak && /* @__PURE__ */ jsx(Button, {
					disabled: loading,
					onClick: () => timeLogAction("endbreak"),
					variant: "main",
					children: /* @__PURE__ */ jsx("div", {
						className: "min-w-max",
						children: "End Break"
					})
				}),
				actions.allowEndShift && /* @__PURE__ */ jsx(ButtonConfirm, {
					confirmationMessage: "Confirm End Shift",
					disabled: loading,
					onClick: () => timeLogAction("endshift"),
					variant: "critical",
					children: /* @__PURE__ */ jsx("div", {
						className: "min-w-max",
						children: "End Shift"
					})
				})
			]
		})]
	});
}
//#endregion
export { ShiftActionsButtonGroup };

//# sourceMappingURL=ShiftActionsButtonGroup.js.map